// One place for what each role is called and what it actually permits.
//
// The permissions below are read off the server's own gates — MUTATING_ROLES
// / POSTING_ROLES / VOIDING_ROLES in the business controllers and
// CLOSING_ROLES in PeriodCloseController — not off the PRD. If those change,
// change these with them.

import { GBBadgeVariant } from '@greenbackapp/ui';
import { BusinessRole } from '../../../types/businessApi';

/**
 * The roles that read the books — the api's BusinessMembership::BOOKS_ROLES.
 * AN ALLOWLIST (PRD 89 §4.10): a role added later reads nothing until it is
 * named here, so the order of this list is also the order the Members tab
 * offers them in.
 */
export const BOOKS_ROLES: readonly BusinessRole[] = [
  'owner',
  'accountant',
  'bookkeeper',
  'ar_clerk',
  'ap_clerk',
  'read_only',
];

/**
 * The field-service roles (PRD 89 §4.7) — the api's
 * BusinessMembership::READ_RESTRICTED_ROLES. The api refuses them every
 * business action nobody named for them, GETs included, with 403
 * `role_forbidden`; the client mirrors that so no screen they can open fires a
 * request the gate refuses.
 */
export const READ_RESTRICTED_ROLES: readonly BusinessRole[] = [
  'dispatcher',
  'technician',
  'register',
  // PRD 121 §4.10: personal services' roles, offered only where the api says
  // they are grantable (set up, held, not paused).
  'front_desk',
  'provider',
];

/**
 * Whether the gate keeps this role out of the books. Anything that is not a
 * books role counts, exactly as the api's `read_restricted?` does, so a role
 * this client has never heard of fails closed rather than open. No role at all
 * (no business yet) is not restricted — there is nothing to restrict.
 */
export const isReadRestrictedRole = (role: string | null | undefined): boolean =>
  role != null && !(BOOKS_ROLES as readonly string[]).includes(role);

export const ROLE_LABELS: Record<BusinessRole, string> = {
  owner: 'Owner',
  accountant: 'Accountant',
  bookkeeper: 'Bookkeeper',
  ar_clerk: 'AR clerk',
  ap_clerk: 'AP clerk',
  read_only: 'Read-only',
  dispatcher: 'Dispatcher',
  technician: 'Technician',
  register: 'Register',
  front_desk: 'Front desk',
  provider: 'Provider',
};

export const ROLE_VARIANTS: Record<BusinessRole, GBBadgeVariant> = {
  owner: 'brand',
  accountant: 'info',
  bookkeeper: 'neutral',
  ar_clerk: 'neutral',
  ap_clerk: 'neutral',
  read_only: 'neutral',
  dispatcher: 'neutral',
  technician: 'neutral',
  register: 'neutral',
  front_desk: 'neutral',
  provider: 'neutral',
};

/** Short line shown next to the role when choosing one. */
export const ROLE_SUMMARIES: Record<BusinessRole, string> = {
  owner: 'Everything, including inviting people and managing the business itself.',
  accountant: 'Full access to the books — posting, journal entries, and closing periods.',
  bookkeeper:
    "Day-to-day bookkeeping. Can't change the chart of accounts, void documents, or close the books.",
  ar_clerk:
    "Money in — customers, invoices, credit memos and customer payments. Can't void anything, touch the supplier side, or close the books.",
  ap_clerk:
    "Money out — vendors, bills, vendor credits, W-9s and bill payments. Can't void anything, touch the customer side, or close the books.",
  read_only: 'View the books and run reports. No changes.',
  // PRD 89 §4.21, word for word with the invitation email's ROLE_DESCRIPTIONS,
  // and plain on purpose: the invitee should know before accepting that this
  // access does not include the books.
  dispatcher:
    'Books and schedules jobs, runs the dispatch board and talks to customers. No books, banking, payroll or reports.',
  technician:
    'Their own jobs on a phone: schedule, status, notes, photos and signatures. No books, banking or reports.',
  // PRD 118 §5.8, word for word with the invitation email: usually a store's
  // own email, not a person's.
  register: "Signs a store's register in. Staff then switch on it with their own PINs. No books, banking or reports.",
  // PRD 121 §4.10, word for word with the invitation email's ROLE_DESCRIPTIONS.
  front_desk: 'Runs the appointment book: calendars, check-in, checkout and clients. No books, banking, payroll or reports.',
  provider: 'Their own appointments, clients and profile on a phone. No books, banking or reports.',
};

/**
 * VOIDING_ROLES on the invoice, bill and payment controllers: who may void.
 * A bulk void is offered only to them. A button that can only be refused
 * reads as a bug.
 */
export const mayVoidDocuments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * MUTATING_ROLES on the customer controller, and on the invoice controller,
 * which lists the same roles: who may change a customer or start an invoice.
 */
export const mayEditCustomers = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/**
 * CustomerDepositsController (PRD 99 §4.12). COLLECT_ROLES request a deposit,
 * record one received, apply it to an invoice, cancel or send a request — the
 * roles that take payments on an invoice. REFUND_ROLES send money back out of
 * the bank. JUDGEMENT_ROLES keep (forfeit) a deposit as income, unapply one,
 * and retry a blocked Stripe event: accounting judgements, as voiding is.
 * Every books member reads the card.
 */
export const mayCollectDeposits = (role: BusinessRole | null | undefined): boolean => mayEditCustomers(role);

export const mayRefundDeposits = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

export const mayJudgeDeposits = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * CustomerContactsController::WRITE_ROLES (PRD 88 §4.12, PRD 89 §4.9): who may
 * add, edit, remove or make primary the people on a customer — the customer
 * writers' own roles plus the dispatcher, who calls the tenant that lets the
 * technician in. Only here, never mayEditCustomers: the customer form itself
 * stays closed to a dispatcher.
 */
export const mayEditCustomerContacts = (role: BusinessRole | null | undefined): boolean =>
  mayEditCustomers(role) || role === 'dispatcher';

/**
 * CustomerContactsController reads: the books roles and the dispatcher
 * (`allow_restricted_roles dispatcher: :all`). The front desk and a provider
 * are refused (role_forbidden), so the customer page draws no Contacts card
 * for them — the Details card already names the primary contact.
 */
export const mayReadCustomerContacts = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'dispatcher');

/**
 * Field::SettingsController::SETTINGS_ROLES (PRD 88 §4.12): who sets field
 * service up and changes its settings. Every books role and the dispatcher
 * READ them; only an owner writes.
 */
export const maySetUpFieldService = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner';

// ---- Personal services (PRD 121 §4.10) ---------------------------------------
//
// Api::V1::Business::Appointments::* literal lists, one helper each.

/**
 * Set up personal services, change its settings, the insurance answer and the
 * booking address: the owner alone. The books roles READ the settings; front
 * desk and provider see the Overview only.
 */
export const maySetUpPersonalServices = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner';

/** Who reads the Settings page: the books roles (§5.2 "read: office roles"). */
export const mayReadPersonalServicesSettings = (role: BusinessRole | null | undefined): boolean =>
  !!role && (BOOKS_ROLES as readonly string[]).includes(role);

/**
 * R7's price-book rule: who creates and edits menu services, categories,
 * add-ons and who performs what (PRD 121 Wave 3's screens).
 */
export const mayManagePsMenu = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/**
 * Who reads the menu (§4.10): every office role (with income account and tax
 * code), the front desk (without them) and a provider (their own services).
 */
export const mayReadPsMenu = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'front_desk' || role === 'provider');

/** Locations, rooms, hours: the owner's settings (Wave 2). The front desk records closures only. */
export const mayManagePsVenues = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** Shifts, and recording and deciding time off for everyone (Wave 2's Staff and Time off pages). */
export const mayScheduleStaff = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher' || role === 'front_desk';

/**
 * StaffEndpoints' READ_ROLES once Wave 2 widens them: every office role, the
 * dispatcher as today, and the front desk. A provider reads only their own row
 * (`me`).
 */
export const mayReadPsRoster = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'dispatcher' || role === 'front_desk');

/** "We're shut — burst pipe" is a desk call: the owner and the front desk record and remove closures. */
export const mayRecordClosures = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/** Who reads the Locations pages: every office role and the front desk; a provider only where they work. */
export const mayReadPsVenues = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'front_desk' || role === 'provider');

// ---- Appointments (PRD 122 §4.17) --------------------------------------------
//
// Api::V1::Business::Appointments::AppointmentEndpoints' literal lists. The
// owner and the front desk run the whole book; Wave 2 opens the calendar to a
// provider — their own column in full, everyone else's as free/busy — and lets
// them place, move and add lines where they are the provider (Dave,
// 2026-10-20). The books roles never see the calendar (invoices and deposits
// reach them through their own pages).

/** CALENDAR_ROLES: the calendar (a provider: own column in full, others free/busy) and an appointment's drawer. */
export const mayReadAppointmentCalendar = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** BOOK_ROLES: New appointment, a drag, *Move…* — a provider only on lines that are theirs (the api's Planner decides). */
export const mayBookAppointments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** Booking for anyone on the calendar, *Anyone* included: the desk. A provider books only themselves. */
export const mayBookForOthers = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/**
 * OVERRIDE_ROLES: *Book anyway?* with a reason, for the overridable conflicts
 * (§4.7.3). A never-code (room taken, cannot perform, …) is refused for
 * everyone; a provider's own two codes go through conflictCopy.overridable.
 */
export const mayOverrideConflicts = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/** CANCEL_ROLES. Never module-gated: a lapse must not strand a booked client. A provider never cancels. */
export const mayCancelAppointments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/** BLOCK_ROLES: blocks and breaks on the calendar — a provider only their own. */
export const mayManageBlocks = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/**
 * TRANSITION_ROLES for check_in, start_service and no_show (Wave 3, PRD 122
 * §4.17's mayRecordNoShow): the desk, and a provider on their own appointments
 * (another's is 404 — Appointments::Scope). Today's outcomes are never
 * module-gated (§4.18).
 */
export const mayRecordAppointmentOutcome = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** CORRECT_ROLES: complete without charge, undo and reinstate — the desk only. */
export const mayCorrectAppointmentStatus = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/** DEPOSIT_REQUEST_ROLES: *Request deposit* on an appointment (PRD 99's RequestService). */
export const mayRequestAppointmentDeposit = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/**
 * SERIES_ROLES (PRD 122 Wave 5, PLAN-W5 ruling 8): *Make standing*, the
 * standing appointments page, *Couldn't book* and its answers, *Change this
 * and following* and *End*. A provider, the finance roles and the field roles
 * are refused (403) on every series endpoint. Writes are module-gated.
 */
export const mayManageStandingAppointments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

// ---- Clients and notes (PRD 122 Wave 4, PLAN-W4 rulings 2–7) -----------------
// The finance roles and the field roles see no appointment notes, no client
// panel and no appointment search at all (403); a provider reads only their
// own clients (another's is 404 — the api's Notes::Visibility and the panel's
// line test decide which clients are theirs).

/** NOTE_READ_ROLES: an appointment's notes (front desk: a private one as a marker; a provider: their own clients). */
export const mayReadAppointmentNotes = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** NOTE_WRITE_ROLES: add a note (a provider on an appointment they have a line on). Module-gated. */
export const mayWriteAppointmentNotes = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** CLIENT_PANEL_ROLES: the drawer's client panel (a provider: their own clients). */
export const mayReadClientPanel = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** QUICK_CREATE_ROLES: *Add as a new client* in the New appointment drawer (a provider, to book their own). Module-gated. */
export const mayQuickCreateClients = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** CLIENT_APPOINTMENTS_ROLES: the customer page's Appointments region, its pages and *Book*. */
export const mayReadClientAppointments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

// ---- Cancellation policies, acceptances, saved cards (PRD 125 §4.11) ---------
//
// Core controllers; the front desk and a provider reach only what each
// controller names for them (RestrictedRoleGate). The PRD's table, W2 rows.

const isBooksRole = (role: BusinessRole | null | undefined): boolean =>
  !!role && (BOOKS_ROLES as readonly string[]).includes(role);

/** READ_ROLES: policies and versions — every office role, the front desk, and a provider (the text only). */
export const mayReadCancellationPolicies = (role: BusinessRole | null | undefined): boolean =>
  isBooksRole(role) || role === 'front_desk' || role === 'provider';

/** PUBLISH_ROLES: publish a version, the fee tax code, proposal expiry — the owner alone. */
export const mayPublishCancellationPolicy = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** ATTEST_ROLES: record that a client agreed by phone or at the desk. */
export const mayAttestPolicyAcceptance = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/**
 * CARD_READ_ROLES: the customer page's Card and policy card — brand, last 4,
 * expiry, the acceptances. Every office role and the front desk; never a
 * provider (contract §5: no client's card).
 */
export const maySeeCardOnFile = (role: BusinessRole | null | undefined): boolean =>
  isBooksRole(role) || role === 'front_desk';

/** CARD_LINK_ROLES: *Send a link to save a card* — not the AP clerk or read-only. */
export const maySendCardLink = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'front_desk';

/** CARD_REMOVE_ROLES: *Remove card — client asked*. The client's own removal needs no role at all. */
export const mayRemoveCardOnRequest = (role: BusinessRole | null | undefined): boolean => maySendCardLink(role);

// ---- Appointment checkout (PRD 126 §4.15; ps-build-126 PLAN ruling 18) -------
//
// Api::V1::Business::Appointments::CheckoutsController (and checkout_lines,
// takings, checkout_settings) literal lists. A provider checks out only an
// appointment with one of their own lines, and only while the venue's
// `providers_check_out_own` is on (403 providers_check_out_own_off) — the
// api's `actions` decide that per ticket; these helpers are the role half.

/** CHECKOUT_ROLES: open, edit, tender, complete and cancel a checkout. */
export const mayRunCheckout = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk' || role === 'provider';

/** READ_ROLES: the ticket, and the completed view (the accountant reads it to void). */
export const mayReadCheckout = (role: BusinessRole | null | undefined): boolean =>
  mayRunCheckout(role) || role === 'accountant';

/** OVER_CAP_ROLES: a discount or price cut beyond the venue's front-desk cap. */
export const mayDiscountOverCap = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** PAY_LATER_ROLES: an invoice with the pay link (only when the venue allows). Never a provider. */
export const mayCheckoutPayLater = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/** VOID_ROLES: void a completed checkout (cash only in W1; a card answers refund_at_processor). */
export const mayVoidCheckout = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/** RECEIPT_ROLES: email the receipt again. */
export const mayResendCheckoutReceipt = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'front_desk';

/** TAKINGS_ROLES: today's takings (the venue, by tender). The desk sees today only; never a provider. */
export const mayReadTakings = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' ||
  role === 'read_only' || role === 'front_desk';

/** SETTINGS_WRITE_ROLES: a location's Checkout card (tax area, pay later, cap, providers, footer). */
export const mayManageCheckoutSettings = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- Tips (PRD 126 §4.15 W2 rows; PLAN ruling 18) ----------------------------
//
// CONTRACT §5, "other people's tips": the desk sees the tip and its split only
// on the checkout in front of it (it has to, to split it) — no Tips view, no
// balances. A provider sees only their own (My tips).

/** CheckoutTipSplitsController: a manual split before completion. Never a provider. */
export const maySplitCheckoutTip = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'front_desk';

/** TIPS_READ_ROLES: the module's Tips view (the books roles). */
export const mayReadCheckoutTips = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'read_only';

/** REALLOCATE_ROLES: re-split a checkout payment's tip after completion. */
export const mayReallocateTips = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/** Void a reported cash tip, with a reason (the owner). */
export const mayVoidReportedCashTip = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** MyTipsController: a provider's own tips; an owner reads their own when they provide (empty otherwise). */
export const mayReadMyTips = (role: BusinessRole | null | undefined): boolean =>
  role === 'provider' || role === 'owner';

// ---- Cancellation fees (PRD 125 §4.11 fee rows; PLAN-W3 ruling 10) -----------
//
// Api::V1::Business::CancellationFeesController's READ_ROLES, CHARGE_ROLES and
// CONFIRM_REFUND_ROLES (reconciled with api-ps-125w3, 2026-11-02). The front desk reaches each
// action only by name in RestrictedRoleGate's allowlist; a provider reaches no
// list at all and sees a fee's STATUS on her own appointments through 122's
// appointment JSON (no amounts). Charge, waive, retry and confirm are never
// module-gated (m1 §4.8, P14).

/** READ_ROLES: the Cancellation fees queue and the fee drawer — every books role and the front desk. */
export const mayReadCancellationFees = (role: BusinessRole | null | undefined): boolean =>
  isBooksRole(role) || role === 'front_desk';

/** CHARGE_ROLES (W3b; `actions.charge/waive` already follow it): charge, reduce or waive a proposed fee; retry its card once. */
export const mayChargeCancellationFees = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'ar_clerk' || role === 'front_desk';

/** CONFIRM_REFUND_ROLES (W3b; a proposal's `actions.confirm/dismiss` follow it): confirm or dismiss a PROPOSED deposit refund. Not the AR clerk. */
export const mayDecideDepositRefundProposals = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'front_desk';

/** On an appointment a provider sees the fee's status only — no amount, no link to the queue. */
export const maySeeCancellationFeeAmounts = (role: BusinessRole | null | undefined): boolean =>
  mayReadCancellationFees(role);

// ---- Service locations (PRD 88 §4.12) ----------------------------------------
//
// Api::V1::Business::Field::LocationEndpoints' literal lists. A location, its
// units, bill-to and contacts are customer master data, so the core customer
// writers keep them and the dispatcher joins them (ruling R7); the gate code is
// the owner's and the dispatcher's alone. A technician reaches a location only
// through a visit assigned to them (PRD 90), and the location page then reads
// what they may do from its own `permissions`, never from these.

/** READ_ROLES: who reads locations and their site contacts. */
export const mayReadLocations = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'dispatcher');

/** WRITE_ROLES: create, edit, units, inactivate, delete, quick create, pin, re-check. */
export const mayEditLocations = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' ||
  role === 'accountant' ||
  role === 'bookkeeper' ||
  role === 'ar_clerk' ||
  role === 'dispatcher';

/**
 * PRD 109 §4.14: importing customers and sites from another field service app
 * (and reading the switching guide) is customer master data — the location
 * writers. Merging two locations is too.
 */
export const mayImportFieldCustomers = (role: BusinessRole | null | undefined): boolean => mayEditLocations(role);
export const mayMergeLocations = (role: BusinessRole | null | undefined): boolean => mayEditLocations(role);

/** FROM_PROPERTY_ROLES (PRD 88 §4.12): the landlord's own doors as locations — not the AR clerk's. */
export const mayCreateLocationsFromProperty = (role: BusinessRole | null | undefined): boolean =>
  mayEditLocations(role) && role !== 'ar_clerk';

/** Rolling back a customers or sites import: owner and accountant (PRD 26 §4.5). */
export const mayRollBackFieldImport = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * PRD 109 §4.14 (Waves 3–4), mirroring FieldService::Imports: jobs land on the
 * board with their crews (booking and assigning, ruling R7), so the owner and
 * the dispatcher import AND roll them back; estimates add the accountant;
 * equipment the office and the dispatcher; memberships the owner and accountant.
 */
export const mayImportFieldJobs = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';
export const mayRollBackFieldJobsImport = mayImportFieldJobs;
export const mayImportFieldEstimates = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'dispatcher';
export const mayImportFieldEquipment = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher';
export const mayImportFieldAgreements = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';
/** Recurring work to set up saves recurrences, which schedule work: owner, dispatcher. */
export const mayReadRecurringWorklist = mayImportFieldJobs;
/** Deposits to check is PRD 99's assign or reclassify, which can post: owner, accountant. */
export const mayReadDepositsWorklist = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * ACCESS_NOTE_ROLES: who reads and writes gate, lockbox and alarm codes —
 * never the accountant, bookkeeper or clerks, which also keeps every firm's
 * staff away from a customer's alarm code (PRD 88 §4.7).
 */
export const maySeeAccessNotes = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/**
 * TAX_AREA_ROLES: PRD 101's sales-tax read roles, the only ones who see a
 * location's tax area. A dispatcher would meet 403 on sales-tax/groups, so the
 * drawer never draws the select for one.
 */
export const maySeeLocationTaxArea = (role: BusinessRole | null | undefined): boolean =>
  !!role && (BOOKS_ROLES as readonly string[]).includes(role);

// ---- Jobs, visits and job types (PRD 90 §4.16) -------------------------------
//
// Api::V1::Business::Field::JobEndpoints' literal lists, one helper each —
// never inferred from canMutate (PRD 88's rule). The defaults are contract
// ruling R7's: field operations (creating jobs, scheduling, assigning,
// dispatching, recording a status on a technician's behalf, editing job types)
// are the owner's and the dispatcher's; the accountant, bookkeeper, clerks and
// read-only READ jobs for their finance work. A technician reads only their
// own jobs (the api answers 404 for anyone else's), so none of these name them.

/** READ_ROLES: who reads jobs, visits, the status log and job types. */
export const mayReadJobs = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'dispatcher');

/** OPERATE_ROLES: create and edit jobs, add visits, edit job types. */
export const mayCreateJobs = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** OPERATE_ROLES: schedule, assign and dispatch visits. */
export const mayDispatch = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** OPERATE_ROLES: record a technician's status on their behalf (Record status). */
export const mayTapOnBehalf = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/**
 * DOCUMENT_ROLES: attach and remove a job's files, through the job's own
 * documents route, while the business holds field service.
 */
export const mayAttachJobDocuments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'ar_clerk' || role === 'dispatcher';

// ---- Estimates (PRD 98 §4.15) --------------------------------------------------
//
// EstimatesController's literal lists, never inferred from canMutate. Every
// office role and the dispatcher READ (mayReadJobs); a technician reads only
// estimates on their own jobs, and in Wave 1 writes none of them.

/** WRITE_ROLES: create, edit, issue, resend and discard estimates; retry a conversion. */
export const mayEditEstimates = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** COST_ROLES: unit cost and option margin (the api omits the keys for everyone else). */
export const maySeeEstimateCosts = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

// ---- The field price book (PRD 97 §4.13) ----------------------------------------
//
// The price-book controllers' literal lists, never inferred from canMutate.
// The page is the office's; a dispatcher or technician reads prices only
// through the job's picker. A TASK's labour cost, estimated cost and margin
// average pay rates, so they follow maySeePayRates.

/** Every office role reads the price book page (the field roles use the picker). */
export const mayReadPriceBook = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' ||
  role === 'ap_clerk' || role === 'read_only';

/** Writes entries, categories, tiers, images, parts, the starter list, re-price and import. */
export const mayEditPriceBook = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** Material cost, markup, material margin, tiers, stale badges and the export. */
export const maySeeCosts = (role: BusinessRole | null | undefined): boolean => mayReadPriceBook(role);

/** RECORD_ROLES: record an approval or decline taken by phone (Wave 2's screen). */
export const mayRecordApprovals = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

// ---- Invoicing a job (PRD 100 §4.15) ------------------------------------------
//
// JobInvoicingController's literal lists. Ruling R7: a dispatcher drafts and
// releases, reads the first two queue tabs, and never issues, sends or sees
// what is owed. A technician reaches none of it in Wave 1.

/** DRAFT_ROLES: draft an invoice from a job, and release lines off a draft. */
export const mayDraftJobInvoices = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'dispatcher';

/** ISSUE_ROLES: issue and send a job's invoice. */
export const mayIssueJobInvoices = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/** PAYMENT_ROLES (LEDGER_ROLES): the Awaiting payment tab and amounts due. */
export const maySeeJobAmountsDue = (role: BusinessRole | null | undefined): boolean =>
  !!role && (BOOKS_ROLES as readonly string[]).includes(role);

/** The settings: the books' roles read, the owner edits. */
export const mayEditInvoicingSettings = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- Job costing (PRD 100 Wave 3–4, §4.15) -------------------------------------
//
// JobCostingController's literal lists. The Profit section and the Job
// profitability report: every office role but the dispatcher. Labour COST and
// the margins after it follow maySeePayRates (owner, accountant, bookkeeper) —
// the api omits them for anyone else. Allocating is AP's too (a bill is AP's
// document); writing parts off is the books' roles'. Field roles see no cost.

/** PROFIT_ROLES: the Profit section and the report. */
export const maySeeJobProfit = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' ||
  role === 'ap_clerk' || role === 'read_only';

/** ALLOCATE_ROLES: allocate a posted cost to a job, or remove it. */
export const mayAllocateJobCosts = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ap_clerk';

/**
 * ReportsController::EMAILING_ROLES and ReportSchedulesController::MANAGING_ROLES:
 * emailing a report or scheduling one decides what leaves the business.
 */
export const mayEmailReports = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/** WRITE_OFF_ROLES: write off or undo the parts a job used. */
export const mayWriteOffJobParts = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

// ---- Booking (PRD 92 §4.13) ----------------------------------------------------
//
// FieldService::Booking's literal allowlists, never inferred from canMutate.
// Every office role and the dispatcher READ the screens (mayReadJobs); every
// booking makes a job, so booking follows PRD 90's job-create row — ruling
// R7's field operations, the owner and the dispatcher. Lead-form routes name
// CRM forms, which PRD 89 closes to the dispatcher. Technicians reach nothing.

/** BOOK_ROLES: book, save a request, file, decline, withdraw and mark spam. */
export const mayBook = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** PAGE_EDIT_ROLES: booking pages and intake questions. */
export const mayEditBookingPages = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** OVERBOOK_ROLES: book a full window, with a reason. */
export const mayOverbook = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** ROUTE_READ_ROLES: read which CRM forms feed field service — every office role but the dispatcher. */
export const mayReadLeadFormRoutes = (role: BusinessRole | null | undefined): boolean =>
  !!role && (BOOKS_ROLES as readonly string[]).includes(role);

/** ROUTE_EDIT_ROLES: route a CRM form into field service — the owner alone. */
export const mayEditLeadFormRoutes = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- The technician's day (PRD 95 §4.18) --------------------------------------
//
// TechnicianDayEndpoints' literal lists. Working one's own day (My day, the
// clock, My time) is anyone with a roster row — the server answers
// `not_a_technician` otherwise — so no helper names it.

/** OTHERS_DAY_ROLES / CORRECT_ROLES: see another technician's day; record and correct time; segment billable. */
export const mayCorrectFieldTime = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/**
 * PRD 95 §4.18: who collects a customer's signature on a visit — the owner,
 * the dispatcher, or a technician on their own visit (the server checks the
 * visit). Voiding one is the office's alone.
 */
export const mayCollectSignatures = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher' || role === 'technician';
export const mayVoidSignatures = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** TIME_READ_ROLES: the day-of-time view, every technician. */
export const mayReadFieldTime = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher';

/** LABOUR_READ_ROLES: a job's Labour section (an AR clerk too — the minutes become invoice lines). */
export const mayReadJobLabour = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'dispatcher';

/** PAY_ROLES: pay rates and labour cost (they run payroll). Never a field role. */
export const maySeePayRates = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** SETTINGS_OPERATIONS_ROLES: the technician-day rules. */
export const mayEditTechnicianDay = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

// ---- Timesheets and the payroll export (PRD 105 §4.16) ------------------------
//
// Api::V1::Business::Field::PayEndpoints' literal lists. Individual pay is
// need-to-know: clerks and read_only see no one's hours. A technician reads
// and submits their own timesheet from My time (the server decides "own").

/** TIMESHEET_READ_ROLES: every hourly technician's timesheet (a dispatcher sees minutes only — there is no money). */
export const mayReadTimesheets = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher';

/** APPROVE_ROLES: approve, return, and reopen before export (§10 item 6). */
export const mayApproveTimesheets = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** EXPORT_ROLES: build, download and void payroll exports. */
export const mayExportPayroll = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** PAY_SETTINGS_READ_ROLES / PAY_SETTINGS_WRITE_ROLES: pay settings and technicians' pay profiles. */
export const mayReadPaySettings = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';
export const mayEditPaySettings = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- Commissions and performance (PRD 105 §4.16, Waves 3–4) -------------------
//
// PayEndpoints' and PerformanceController's literal lists. Commission is
// individual pay: never a clerk, read_only or a field role (R7). A technician
// reads their own plan, statement and numbers from My numbers (the server
// decides "own", and the business may turn it off).

/** COMMISSION_MANAGE_ROLES: plans, rules, splits, manual adjustments and forgiveness. */
export const mayManageCommissions = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** COMMISSION_READ_ROLES: the register, holds, roll-forward, preview and a job's Commission section. */
export const maySeeCommissions = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** RELIEF_ROLES: record commission relief on an export, or link the payroll run that relieved it. */
export const mayRecordCommissionRelief = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/** PerformanceController::READ_ROLES: the performance page and scorecards (a dispatcher without money columns). */
export const maySeePerformance = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only' || role === 'dispatcher';

// ---- The field roster (PRD 89 §4.18) -----------------------------------------
//
// Api::V1::Business::Field::StaffEndpoints' literal lists, one helper each.
// The defaults are contract ruling R7's: the roster, skills, zones, shifts,
// time off and vans are operating settings the owner and the dispatcher keep;
// accountants and bookkeepers read them for costing and never dispatch. A
// technician reaches none of these screens (their own row is Wave 3's My
// profile), so none of these names them.

/** READ_ROLES: who reads the roster, skills, zones, shifts, time off and vans. */
export const mayReadFieldRoster = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'dispatcher');

/** MANAGE_ROLES: add, edit and archive technicians; skills, zones, shifts, vans; record and decide time off. */
export const mayManageTechnicians = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** COST_READ_ROLES: who reads a technician's labour cost rate. Never a field role (R7). */
export const maySeeLabourCost = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** COST_WRITE_ROLES: who sets one. */
export const maySetLabourCost = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** LOGIN_ROLES: who invites, links and unlinks a technician's login. */
export const mayInviteFieldStaff = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** CENSUS_ROLES: who reads the roster's "count this month" line. */
export const maySeeTechnicianCensus = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher';

/**
 * CENSUS_DETAIL_ROLES: who sees which technicians count toward billing, and
 * why. A dispatcher reads the count only (§4.18).
 */
export const maySeeTechnicianBilling = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** FixedAssetsController::MUTATING_ROLES: who sees and sets the fixed asset a van is booked as. */
export const mayLinkVehicleAsset = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/**
 * MUTATING_ROLES on the vendor controller, and on the bill and W-9 request
 * controllers, which list the same roles: who may change a vendor, ask them
 * for a W-9, or start a bill.
 */
export const mayEditVendors = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ap_clerk';

/**
 * DOCUMENT_ROLES on the W-9 submission controller: who may download a signed
 * W-9, which carries an unmasked SSN. Narrower than mayEditVendors: the AP
 * clerk may ask for a W-9 but not download the one that comes back.
 */
export const mayDownloadW9s = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/**
 * Filings1099Controller::MUTATING_ROLES, which the payment 1099
 * classification endpoint reuses: who may finalize, void or mark a 1099
 * filing, and decide one payment's 1099 treatment.
 */
export const mayFile1099s = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/** POSTING_ROLES on the journal entry controller: who may post or reverse one. */
export const mayReverseEntries = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * EDIT_ROLES in the api's SalesTaxEndpoints (PRD 101 §4.12): who may enter
 * jurisdictions, rates, tax areas and tax codes, answer the setup questions
 * and set the defaults — the roles that manage service items. Never derived
 * from canMutate: a read-only member of a paying business still cannot.
 */
export const mayEditSalesTax = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/**
 * ACCOUNT_ROLES in SalesTaxEndpoints: who may turn sales tax on or off and
 * choose the account it posts to — an accounting decision about how the
 * business bills, like document numbering.
 */
export const mayTurnOnSalesTax = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * EDIT_ROLES again, for Wave 3's writes (PRD 101 §4.12): who may record a
 * payment to the state, add a registration, and attribute a hand-posted tax
 * line — the roles that categorise the bank line the payment came from.
 */
export const mayRecordTaxPayment = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** VOIDING_ROLES on SalesTaxRemittancesController: voiding is a void like any other. */
export const mayVoidTaxPayment = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/**
 * ProspectsController::WRITE_ROLES: who works the CRM pipeline — moves a card,
 * adds a prospect, logs an activity.
 *
 * SPELT OUT, never "everyone but read-only" (PRD 89 §4.11). A subtraction
 * hands CRM writes to every role added after it, which is exactly how the
 * field roles would have got them; the api's list was rewritten the same way.
 */
export const mayWriteProspects = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' ||
  role === 'accountant' ||
  role === 'bookkeeper' ||
  role === 'ar_clerk' ||
  role === 'ap_clerk';

export interface RoleOption {
  value: BusinessRole;
  label: string;
}

/** The books roles, in the order they are offered. */
export const ROLE_OPTIONS: RoleOption[] = BOOKS_ROLES.map(role => ({
  value: role,
  label: ROLE_LABELS[role],
}));

/**
 * The field roles, offered after the books roles and only where they can be
 * granted (PRD 89 §4.21) — see grantableRoleOptions.
 */
export const FIELD_ROLE_OPTIONS: RoleOption[] = READ_RESTRICTED_ROLES.map(role => ({
  value: role,
  label: ROLE_LABELS[role],
}));

/**
 * The roles an owner may pick here: every role the api says is grantable in
 * this business today (meta.grantable_roles), books roles first.
 *
 * `current` is the role the member already holds, offered even when it could
 * not be granted again today — a dispatcher in a business whose field service
 * has lapsed keeps the role (PRD 89 §4.13), and a select that cannot show its
 * own value would read as a different one. `fieldRoles: false` leaves the
 * field roles out for a membership the firm provisioned: the api refuses them
 * there with `role_unavailable`, because the firm layer would re-provision the
 * row and never assigns a field role (§4.1).
 */
export const grantableRoleOptions = (
  grantable: readonly BusinessRole[],
  { current, fieldRoles = true }: { current?: BusinessRole; fieldRoles?: boolean } = {}
): RoleOption[] =>
  [...ROLE_OPTIONS, ...FIELD_ROLE_OPTIONS].filter(
    option =>
      option.value === current ||
      (grantable.includes(option.value) && (fieldRoles || !isReadRestrictedRole(option.value)))
  );

export interface Capability {
  label: string;
  roles: BusinessRole[];
  /**
   * A field-service capability: drawn only beside the field roles' columns,
   * which appear only where those roles can be granted.
   */
  field?: boolean;
  /** A personal services capability (PRD 121 §4.10), beside the front desk and provider columns. */
  personalServices?: boolean;
}

export const CAPABILITIES: Capability[] = [
  {
    label: 'View the books, run reports, export',
    roles: ['owner', 'accountant', 'bookkeeper', 'ar_clerk', 'ap_clerk', 'read_only'],
  },
  { label: 'Categorize, post, match and ignore bank transactions', roles: ['owner', 'accountant', 'bookkeeper'] },
  {
    label: 'Create customers, invoices, credit memos and deposits',
    roles: ['owner', 'accountant', 'bookkeeper', 'ar_clerk'],
  },
  {
    label: 'Create vendors, bills, vendor credits and W-9 requests',
    roles: ['owner', 'accountant', 'bookkeeper', 'ap_clerk'],
  },
  { label: 'Edit the chart of accounts', roles: ['owner', 'accountant'] },
  { label: 'Turn sales tax on or off, and choose its account', roles: ['owner', 'accountant'] },
  {
    label: 'Set up sales tax rates, tax areas and tax codes',
    roles: ['owner', 'accountant', 'bookkeeper'],
  },
  {
    label: 'Record payments to the state and sales tax registrations',
    roles: ['owner', 'accountant', 'bookkeeper'],
  },
  { label: 'Post and reverse manual journal entries', roles: ['owner', 'accountant'] },
  { label: 'Void invoices, bills and payments (sales tax payments too)', roles: ['owner', 'accountant'] },
  { label: 'Close and reopen periods', roles: ['owner', 'accountant'] },
  { label: 'Force-close past blockers', roles: ['owner'] },
  { label: 'Invite, remove and change members', roles: ['owner'] },
  { label: 'Edit the business profile', roles: ['owner'] },
  // The field roles' own rows (PRD 89 §4.7, ruling R7). Every row above
  // excludes both of them, which is the point: they never see the books.
  {
    label: 'Look up customers and products & services (a dispatcher sees no balances)',
    roles: [...BOOKS_ROLES, 'dispatcher'],
    field: true,
  },
  { label: 'Book, schedule and dispatch field jobs', roles: ['owner', 'dispatcher'], field: true },
  {
    label: 'Work the visits assigned to them, once on the field roster',
    roles: ['owner', 'dispatcher', 'technician'],
    field: true,
  },
  // Personal services' rows (PRD 121 §4.10 — what this wave and the next two
  // open to them). Every books row above excludes both roles, as for field.
  {
    label: 'Set up personal services, its settings and the booking address',
    roles: ['owner'],
    personalServices: true,
  },
  {
    label: 'Run the appointment book: everyone\'s shifts and time off',
    roles: ['owner', 'front_desk'],
    personalServices: true,
  },
  {
    label: 'Edit the service menu and its prices',
    roles: ['owner', 'accountant', 'bookkeeper'],
    personalServices: true,
  },
  {
    label: 'Their own appointments, week and profile, once on the staff list',
    roles: ['owner', 'front_desk', 'provider'],
    personalServices: true,
  },
];

// ---- The customer hub (PRD 107 §4.11, ruling R7) --------------------------------
//
// FieldService::Hub's literal lists. The link is an account credential as much
// as a field one — it carries the invoices an AR clerk chases — so the AR clerk
// may email, copy, print and reset it, but not text it; handling a request is
// booking work, the owner's and the dispatcher's. Never inferred from canMutate.

/** READ_ROLES: the hub card, View as customer, Recent access, the settings tab, requests. */
export const mayReadCustomerHub = (role: BusinessRole | null | undefined): boolean =>
  !!role && ((BOOKS_ROLES as readonly string[]).includes(role) || role === 'dispatcher');

/** MANAGE_ROLES: email, copy and print the link; reset, turn off, turn on. */
export const mayManageCustomerHub = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'ar_clerk' || role === 'dispatcher';

/** TEXT_ROLES: send the link by text. */
export const mayTextCustomerHub = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** REQUEST_ROLES: set the place, put in the tray, book, decline, record a withdrawal. */
export const mayHandleServiceRequests = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** SETTINGS_WRITE_ROLES: the hub settings tab. */
export const mayEditCustomerHubSettings = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- Customer feedback (PRD 104 §4.12, ruling R7) -------------------------------
//
// FieldService::Feedback's literal lists. Every office role but the AP clerk
// and the dispatcher read feedback; contacting the customer, booking a return
// and turning a recommendation into a job are field operations (owner,
// dispatcher); the AR roles link the credit a recovery ends in. A technician
// reads no feedback at all — only their own recommendations.

/** FEEDBACK_READ_ROLES: settings, asks, responses, recovery, the report. */
export const mayReadFeedback = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' ||
  role === 'read_only' || role === 'dispatcher';

/** RECOVERY_WRITE_ROLES: open, assign, contact, book a return, resolve, close; resend a failed ask. */
export const mayManageRecovery = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** TECHNICIAN_REPORT_ROLES: ratings by technician (sorted by name, never ranked). */
export const maySeeTechnicianRatings = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** RECOMMENDATION_ESTIMATE_ROLES less the technician: turn a recommendation into an estimate; dismiss. */
export const mayConvertRecommendations = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'ar_clerk' || role === 'dispatcher';

/** CREDIT_LINK_ROLES: link the credit memo a recovery ended in (core's credit-memo roles). */
export const mayLinkRecoveryCredit = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/** EXCLUSION_RECORD_ROLES: record a customer's "don't ask for feedback". */
export const mayRecordFeedbackExclusion = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'ar_clerk' || role === 'dispatcher';

// ---- Memberships (PRD 102 §4.13) ------------------------------------------------
//
// Literal mirrors of FieldService::Agreements' role lists. The server decides
// regardless; these only keep a button from being offered that it refuses.

/** READ_ROLES: plans, memberships and the badge (a dispatcher without money). */
export const mayReadMemberships = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only' || role === 'dispatcher';

/** The owner edits plans; accountant and bookkeeper their accounting fields only. */
export const mayEditPlans = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** SELL_ROLES: draft, present, take a signature, record a phone sale. */
export const maySellMemberships = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'ar_clerk' || role === 'dispatcher';

/** ACTIVATE_ROLES — the invoice roles: start billing (it issues the first invoice). */
export const mayManageMemberships = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/** REPORT_ROLES: the deferred revenue roll-forward and an agreement's revenue (no dispatcher, no technician). */
export const maySeeDeferredRevenue = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only';

// ---- Field payments: collections and tips (PRD 99 §4.12, Wave 3) -------------
//
// Literal mirrors of Field::CollectionsController and Field::TipsController,
// and core's TipPayoutsController. The dispatcher takes no money and reads none
// of it; a technician reads their own collections and tips (the server decides
// "own").

/** LIST_ROLES / the Tips tab's readers: every books role. */
export const mayReadFieldPayments = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only';

/** HAND_OVER_ROLES: mark a collection received by the office (custody — nothing posts). */
export const mayHandOverCollections = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/** REALLOCATE_ROLES (field) and PAYOUT_ROLES (core): split a tip by hand, pay tips out of the bank. */
export const mayManageTips = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

// Technician location (PRD 96 §4.13), mirroring the api's allowlist. Your
// OWN location (My location, its notice, consent, own history, who looked)
// is anyone's with a linked roster row, so it needs no helper here.
/** The live map's technician layer: owner and dispatcher. */
export const mayViewPositions = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** Location settings, publishing the notice, paper acknowledgements, history access and purge: the owner. */
export const mayManageLocation = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** Reading the location settings page (no writes): owner and dispatcher. */
export const mayReadLocationSettings = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** Opening ANOTHER technician's history: an owner, or a dispatcher the owner named (the api checks the grant). */
export const mayAskLocationHistory = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

// The mileage log (PRD 96 §4.13), mirroring the api.
/** Read trips: books roles and the dispatcher (miles only); a technician reads their own. */
export const mayReadMileage = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher' || role === 'technician';

/** Confirm or void a trip, and read the report: owner, accountant, bookkeeper. */
export const mayConfirmMileage = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** Post or reverse a reimbursement, edit mileage settings: owner and accountant (JournalEntries' POSTING_ROLES). */
export const mayReimburseMileage = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

// ---- Card readers (PRD 99 §4.12, Wave 4) ------------------------------------
//
// Literal mirrors of Field::TerminalReadersController. The dispatcher takes no
// money; a technician sends his own visit's invoice to a reader only while the
// owner's technician invoicing is `collect` or `issue` (the api's Invoice step
// says so with `card_reader`).

/** LIST_ROLES: every books role reads the business's readers. */
export const mayReadCardReaders = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only';

/** MANAGE_ROLES: register, rename, move to a van and remove a reader. */
export const mayManageCardReaders = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** COLLECT_ROLES: the office roles that send an invoice to a reader. */
export const mayCollectOnReader = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

// ---- Consumer financing, bring your own lender (PRD 99 §4.12, Wave 5) --------
//
// Literal mirrors of Api::V1::Business::Field::FinancingEndpoints. The
// agreement's file and terms are the owner's, the accountant's and the
// bookkeeper's; those who offer financing see lenders' names only. The
// dispatcher offers (no money moves) but records nothing; a technician offers
// on an assigned job while technician invoicing is collect or issue (the api
// checks that).

/** FINANCING_MANAGE_ROLES: add or archive a lender; upload its agreement and confirm its terms. */
export const mayManageFinancing = (role: BusinessRole | null | undefined): boolean => role === 'owner';

/** FINANCING_TERMS_ROLES: read a lender's agreement file and terms. */
export const mayReadFinancingTerms = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** FINANCING_READ_ROLES: the Financing tab. */
export const mayReadFinancing = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only';

/** FINANCING_OFFER_ROLES (plus a collecting technician, which the api decides). */
export const mayOfferFinancing = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'dispatcher' ||
  role === 'technician';

/** FINANCING_RECORD_ROLES: record an approval, a funding, a cancellation or a stated fee; match a lender's deposit. */
export const mayRecordFinancing = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/** FINANCING_ACKNOWLEDGE_ROLES: accept a lender's stated fee that differs from the agreement, so its held deposit posts. */
export const mayAcknowledgeFinancingFee = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- Install projects (PRD 110 §4.12) ----------------------------------------
// Mirrors Api::V1::Business::Field::ProjectEndpoints — never read off canMutate.

/** PROJECT_FIELD_ROLES: create a project and its phases, attach jobs, mark it complete (contract R7). */
export const mayEditProjects = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** PROJECT_BILL_ROLES (InvoicesController::MUTATING_ROLES): bill in stages, the schedule, draft and issue bills, close. */
export const mayBillProjects = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk';

/** PROJECT_MONEY_ROLES: a project's schedule, bills, change orders and costs. */
export const maySeeProjectMoney = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only';

/** Record a change order with the customer's document (Wave 2). */
export const mayRecordChangeOrders = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'ar_clerk';

/** Void a change order (Wave 2). */
export const mayVoidChangeOrders = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';

/** COST_ROLES: the estimate at completion, designations and parts used (Wave 2). */
export const mayManageProjectCosts = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

// ---- Equipment, permits and site history (PRD 106 §4.11) ---------------------
//
// Literal mirrors of Field::EquipmentEndpoints. Field operations are the owner
// and the dispatcher (plus a technician on their own visit — the api answers
// for that, per place); the finance roles read. Never inferred from canMutate.

/** READ_ROLES: equipment, warranties, history, the refrigerant log, permits, opportunities. */
export const mayReadEquipment = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only' || role === 'dispatcher';

/** WRITE_ROLES: record, edit, remove and delete units; types; snooze and dismiss opportunities. */
export const mayEditEquipment = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** REFRIGERANT_ROLES: record on behalf of a technician (a technician records their own on a live visit). */
export const mayRecordRefrigerant = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher' || role === 'technician';

/** REFRIGERANT_EXPORT_ROLES: download the refrigerant log. */
export const mayExportRefrigerantLog = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher';

/** PERMIT_ROLES: create, edit and move permits; schedule inspections. */
export const mayManagePermits = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'dispatcher';

/** The settings page: everyone in the office reads; the owner edits. */
export const mayEditEquipmentSettings = (role: BusinessRole | null | undefined): boolean => role === 'owner';

// ---- Truck stock and purchasing (PRD 103 §4.15) -----------------------------
//
// Literal mirrors of FieldService::Stock / ::Purchasing role lists. The
// dispatcher works quantities (never a cost or value); a technician his own
// van — the api answers for that. Never inferred from canMutate.

/** READ_ROLES: quantities, moves, transfers, counts, rules, POs. */
export const mayReadStock = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only' || role === 'dispatcher';

/** COST_ROLES: costs, values, PPV, the accrual. */
export const maySeeStockValue = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ar_clerk' || role === 'ap_clerk' ||
  role === 'read_only';

/** SETUP_ROLES: set up, locations, settings; post or cancel a count. */
export const maySetUpStock = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';

/** MOVE_ROLES: rules; create, complete and cancel transfers; open and submit counts. */
export const mayManageStock = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'dispatcher';

/** PO_ROLES: create, edit, send, cancel, close POs; Order the parts; draft the bill; returns. */
export const mayPurchase = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'ap_clerk';

/** RECEIVE_ROLES (a technician receives POs to his van when the business allows it). */
export const mayReceiveStock = (role: BusinessRole | null | undefined): boolean =>
  mayPurchase(role) || role === 'dispatcher';

/** VOID_RECEIPT_ROLES. */
export const mayVoidStockReceipt = (role: BusinessRole | null | undefined): boolean =>
  role === 'owner' || role === 'accountant';
